import {
  buildLoginPayload,
  confirmSignIn,
  resetPassword as resetPasswordApi,
  signIn,
} from '../utils/amplifyAuthHelpers';
import { Alert, AlertDescription } from '../ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import * as yup from 'yup';
import { Actions as LOGINACTIONS } from '../../store/actions/authenticationActions';
import { X, Loader2, Info, Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import LogoSide from '../Register/LogoSide';
import ValidationMessage from '../utils/ValidationMessage';
import AuthDropdown from './AuthDropdown';
import SetNewPassword from './SetNewPassword';

const validationSchema = yup.object().shape({ email: yup.string().email().required('Email is required') });
const validationNewPassword = yup.object().shape({
  newPassword: yup
    .string()
    .required('Password is required')
    .min(8, 'Password is too short - should be 8 chars minimum.')
    .matches(
      /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-])\S{8,}/,
      'Password must contain 8 characters, one uppercase, one lowercase, one number and one special case character'
    ),
});

const CYFLoginPage = ({
  branding,
  invalidLoginCredentials,
  loginWithAws,
  processing,
  getUserCredentials,
  loggingIn,
  pathname,
  supportEmail,
  hasPaymentSystem,
}) => {
  const { btnTextColor, primaryHeaderText, signInFormBg, primaryColor, tooltipBackground, tooltipText } =
    branding.colors || {};
  const { headers, body } = branding.fonts?.[0].font || {};
  const accountCode = new URLSearchParams(window.location.href).get('code');

  const [editedState, setEditedState] = useState({
    username: '',
    password: '',
  });
  const [resetPassword, setResetPassword] = useState(pathname.includes('resetPassword'));
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState({ password: false, newPassword: false });
  const [error, setError] = useState(null);
  const [requiresNewPassword, setRequiresNewPassword] = useState(false);
  const [showNewPasswordComponent, setShowNewPasswordComponent] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [alertStatus, setAlertStatus] = useState(false);
  const alertMessage =
    'To successfully update your password, please complete the password reset process within 48 hours upon receipt of the verification code.';

  const errorsToDisplay = {};

  if (accountCode) {
    // If we have account code here, it's because an ambassador has just connected their account to stripe.
    // Store the accountCode on local storage.
    localStorage.setItem('accountCode', accountCode);
  }

  validationError?.forEach((error) => {
    if (error.path === 'email') {
      errorsToDisplay['email'] = error.message;
    } else if (error.path === 'newPassword') {
      errorsToDisplay['newPassword'] = error.message;
    }
  });

  const forgotPassword = async () => {
    await validationSchema
      .validate({ email: editedState?.['email'] }, { abortEarly: false })
      .then(async () => {
        try {
          loggingIn(true);
          await resetPasswordApi({ username: editedState?.['email'].trim() });
          setShowNewPasswordComponent(true);
          loggingIn(false);
        } catch (error) {
          console.log(error);
          loggingIn(false);
          setError(error);
        }
      })
      .catch((error) => {
        console.log('Validation Error', error);
        loggingIn(false);
        setValidationError(error.inner);
      });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    try {
      loggingIn(true);

      if (requiresNewPassword) {
        await validationNewPassword.validate({ newPassword }, { abortEarly: false });
        const confirmOutput = await confirmSignIn({ challengeResponse: newPassword.trim() });
        if (!confirmOutput.isSignedIn) {
          throw new Error('Password was updated, but sign in could not be completed. Please try again.');
        }
        const user = await buildLoginPayload();
        await loginWithAws(user);
        getUserCredentials();
        return;
      }

      const signInOutput = await signIn({
        username: editedState?.['username']?.trim(),
        password: editedState?.['password']?.trim(),
      });

      if (signInOutput.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        if (pathname.includes('inviteuser') && newPassword) {
          await validationNewPassword.validate({ newPassword }, { abortEarly: false });
          const confirmOutput = await confirmSignIn({ challengeResponse: newPassword.trim() });
          if (!confirmOutput.isSignedIn) {
            throw new Error('Password was updated, but sign in could not be completed. Please try again.');
          }
          const user = await buildLoginPayload();
          await loginWithAws(user);
          getUserCredentials();
          return;
        }

        setRequiresNewPassword(true);
        setError(null);
        setValidationError(null);
        loggingIn(false);
        return;
      }

      if (signInOutput.isSignedIn) {
        const user = await buildLoginPayload();
        loginWithAws(user);
        getUserCredentials();
        return;
      }

      loggingIn(false);
      setError({ message: 'Sign in could not be completed. Please try again.' });
    } catch (error) {
      loggingIn(false);
      console.log('signInError', error);
      const err = error?.message ? error : { message: error };
      setError(err);
      if (error?.inner) setValidationError(error.inner);
    }
  };

  const updateField = (e) => {
    const newEditedState = { ...editedState };
    newEditedState[e.target.name] = e.target.value;
    setEditedState(newEditedState);
  };

  if (showNewPasswordComponent) {
    return <SetNewPassword setReset={setResetPassword} setShowNewPasswordComponent={setShowNewPasswordComponent} />;
  } else if (resetPassword) {
    return (
      <div className='login-body' style={{ height: '100vh', width: '100%', display: 'flex' }}>
        <LogoSide />
        <div className='signInSide' style={{ background: signInFormBg, fontFamily: body }}>
          <div className='signInFields' style={{ width: '35vw' }}>
            <h1 style={{ marginBottom: '1.5vh', color: primaryHeaderText }}>Forgot Password?</h1>
            <div>
              <div
                className='col-xs-12 alert-danger alert'
                style={{ marginBottom: 10, zIndex: 10, display: error ? 'block' : 'none' }}>
                <h5 style={{ textAlign: 'left' }}>Error: {error?.log || error?.message}</h5>
              </div>

              <div style={{ marginBottom: '1.5vh', width: '100%' }}>
                <p style={{ marginBottom: '1.5vh', color: primaryHeaderText }}>
                  Enter your e-mail address below to reset your password.
                </p>
                <Input
                  id='email-input'
                  name='email'
                  className='h-12'
                  onChange={updateField}
                  onClick={() => setAlertStatus(true)}
                  placeholder='Email'
                  type='text'
                />

                {alertStatus && (
                  <Alert variant='warning' style={{ marginTop: '3px', fontFamily: body }}>
                    <AlertDescription>
                      <b>{alertMessage}</b>
                    </AlertDescription>
                    <button onClick={() => setAlertStatus(false)} className='ml-auto pl-2 hover:opacity-70'>
                      <X size={16} strokeWidth={1.5} />
                    </button>
                  </Alert>
                )}

                <ValidationMessage message={errorsToDisplay.email} />
              </div>

              <div style={{ fontFamily: headers }} className='flex gap-2'>
                <Button
                  type='button'
                  variant='outline'
                  id='cancelBtn-forgot-password'
                  onClick={() => {
                    setResetPassword(false);
                    setValidationError(null);
                    setAlertStatus(false);
                  }}>
                  Cancel
                </Button>

                <Button
                  size='lg'
                  id='forgot-password-submit-button'
                  className='pull-right'
                  style={{ color: '#FFFF' }}
                  onClick={forgotPassword}>
                  {processing && <Loader2 size={16} strokeWidth={1.5} className="animate-spin" />}
                  Submit
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className='login-body w-full block md:flex flex-col md:flex-row remove-topMargin'
      style={{ height: '100vh', fontFamily: body }}>
      <LogoSide />
      <div className='signInSide w-full md:w-1/2 md:h-full items-center' style={{ background: signInFormBg }}>
        <div className='w-4/5 md:w-2/3'>
          <h1 className='h1' style={{ marginBottom: '3vh', color: primaryHeaderText }}>
            Sign In
          </h1>
          <form method='post' noValidate='novalidate' onSubmit={handleSubmit}>
            <div>
              <div>
                <div className='relative mb-6'>
                  <Input
                    id='email'
                    type='text'
                    name='username'
                    placeholder='Email'
                    autoFocus
                    value={editedState['username'] || ''}
                    onChange={updateField}
                    data-lpignore='true'
                    className='h-12 pr-10 mb-6'
                  />
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className='absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground'>
                          <Info size={16} strokeWidth={1.5} />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side='left'>
                        <span style={{ fontSize: 12 }}> Email addresses are case sensitive throughout the application. </span>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>

                <div className='relative mb-6'>
                  <Input
                    id='password'
                    type={showPassword.password ? 'text' : 'password'}
                    name='password'
                    value={editedState && editedState['password']}
                    onChange={updateField}
                    placeholder={pathname.includes('inviteuser') ? 'Temporary Password' : 'Password'}
                    data-lpignore='true'
                    className='h-12 pr-10 mb-6'
                  />
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    className='absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8'
                    onClick={() => setShowPassword({ ...showPassword, password: !showPassword.password })}
                    aria-label={showPassword.password ? 'Hide password' : 'Show password'}>
                    {showPassword.password ? <Eye size={16} strokeWidth={1.5} /> : <EyeOff size={16} strokeWidth={1.5} />}
                  </Button>
                </div>

                {(pathname.includes('inviteuser') || requiresNewPassword) && (
                  <>
                    {requiresNewPassword && (
                      <Alert className='mb-4' variant={error ? 'destructive' : undefined}>
                        <AlertDescription>
                          {error?.message ||
                            'Your temporary password was accepted. Choose a permanent password to finish signing in.'}
                        </AlertDescription>
                      </Alert>
                    )}
                    <div className='relative mb-4'>
                      <Input
                        id='newPassword'
                        type={showPassword.newPassword ? 'text' : 'password'}
                        name='newPassword'
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder='New Password'
                        className='h-12 pr-10'
                      />
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        className='absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8'
                        onClick={() => setShowPassword({ ...showPassword, newPassword: !showPassword.newPassword })}
                        aria-label={showPassword.newPassword ? 'Hide password' : 'Show password'}>
                        {showPassword.newPassword ? <Eye size={16} strokeWidth={1.5} /> : <EyeOff size={16} strokeWidth={1.5} />}
                      </Button>
                    </div>
                    {errorsToDisplay.newPassword && (
                      <Alert variant='destructive' style={{ color: 'red' }}>
                        <AlertDescription>
                          <ValidationMessage style={{ marginBottom: '4px' }} message={errorsToDisplay.newPassword} />
                        </AlertDescription>
                      </Alert>
                    )}
                  </>
                )}
              </div>

              {(invalidLoginCredentials || error) && !requiresNewPassword && (
                <div
                  className='alert-danger alert'
                  style={{
                    marginTop: '3vh',
                    display: pathname.includes('inviteuser') ? 'none' : 'block',
                  }}>
                  <h5 style={{ textAlign: 'left' }}> Incorrect Username or Password</h5>
                  <ul style={{ listStyle: 'disc', paddingLeft: '20px', textAlign: 'left' }}>
                    <li>Are you using the right email address?</li>
                    <li>Is the first letter of your email address in lowercase?</li>
                    <li>If you are unable to log-in, resetPassword your password below.</li>
                  </ul>
                </div>
              )}
              <div className='login-button-section flex justify-between flex-col md:flex-row' style={{ marginTop: '3vh' }}>
                <div className='w-full md:w-1/2'>
                  <Button
                    size='lg'
                    type='submit'
                    disabled={processing || !editedState.username || !editedState.password}
                    className='w-full'
                    style={{ fontFamily: headers, color: 'white' }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSubmit();
                    }}>
                    {processing && <Loader2 size={16} strokeWidth={1.5} className="animate-spin" id='login-spinner' />}
                    {requiresNewPassword ? 'Set New Password' : 'Sign In'}
                  </Button>
                </div>
                <div className='w-full md:w-1/2' style={{ position: 'relative' }}>
                  <AuthDropdown headers={headers} />
                </div>
              </div>

              <div className='mt-5 flex justify-between'>
                <Button
                  type='button'
                  variant='link'
                  className='w-1/3 p-0 h-auto text-left'
                  id='forgot-password'
                  onClick={() => setResetPassword(true)}
                  style={{ fontFamily: body, color: btnTextColor }}>
                  Forgot Password?
                </Button>
                <a
                  className='w-1/3 text-center text-sm text-primary hover:underline'
                  href={`mailto:${supportEmail}`}
                  style={{ fontFamily: body, color: btnTextColor }}>
                  Need support?
                </a>
                <a
                  className='w-1/3 text-right text-sm text-primary hover:underline'
                  href='/register'
                  style={{
                    display: hasPaymentSystem ? 'block' : 'none',
                    cursor: 'pointer',
                    color: btnTextColor,
                    textAlign: 'right',
                  }}
                  target='_blank'
                  rel='noopener noreferrer'>
                  Don't have an account yet?
                </a>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  const pathname = window.location.pathname;
  const { supportEmail, hasPaymentSystem } = state.whiteLabelAdmin;
  return {
    profile: state?.profile?.details,
    invalidLoginCredentials: state.authentication.invalidLoginCredentials,
    processing: state.authentication.processing,
    branding: state.branding,
    supportEmail,
    pathname,
    hasPaymentSystem,
  };
};

const mapDispatchToProps = (dispatch) => ({
  loginWithAws: (username) => dispatch(LOGINACTIONS.loginWithAws(username)),
  loggingIn: (saving) => dispatch(LOGINACTIONS.loggingIn(saving)),
  getUserCredentials: () => dispatch(LOGINACTIONS.getUserCredentials()),
});

export default connect(mapStateToProps, mapDispatchToProps)(CYFLoginPage);
